import React, { useState, useEffect, useRef } from 'react';
import { authAPI } from '../../services/api';
import TermsModal from './TermsModal';

const inputClass = "w-full border border-[#E5E7EB] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/15 focus:border-[#1E40AF] bg-white transition-all";
const pinInputClass = `${inputClass} text-center text-2xl font-bold tracking-[0.5em]`;

// New-account registration via WhatsApp — mirrors passenger_app's
// login_screen.dart + pin_create_screen.dart. The passenger sends
// a short code TO Safer's WhatsApp number (not the other way around); see
// backend/src/services/verificationService.js's header comment for why
// (avoids Meta's business-initiated-message template restriction).
// 'form' → name + phone. 'waiting' → show code, poll for VERIFIED. 'pin' → set PIN.
const WhatsAppRegisterPanel = ({ navigate, returnTo }) => {
  const [step, setStep] = useState('form');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [verificationId, setVerificationId] = useState(null);
  const [code, setCode] = useState(null);
  const [deepLink, setDeepLink] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const friendlyError = (err) => err.response?.data?.message || 'حدث خطأ، حاول مرة أخرى';

  const nameWordCount = name.trim().split(/\s+/).filter(Boolean).length;
  const isFullName = nameWordCount >= 4;

  const start = async (e) => {
    e.preventDefault();
    if (!isFullName) {
      setError('يرجى إدخال الاسم الرباعي الكامل (4 كلمات على الأقل)'); return;
    }
    if (!phone.trim()) { setError('يرجى إدخال رقم الهاتف'); return; }
    setError(''); setLoading(true);
    // Reserved synchronously, inside this click handler — a tab opened
    // AFTER the await below (once the API responds) is no longer inside
    // the "user gesture" window most browsers require, and gets silently
    // popup-blocked. Redirecting this already-open tab once we have the
    // real link avoids that.
    const whatsappWindow = window.open('', '_blank');
    try {
      const res = await authAPI.startWhatsAppVerification({ phone: phone.trim(), name: name.trim() });
      const data = res.data.data;
      setVerificationId(data.verificationId);
      setCode(data.code);
      setDeepLink(data.deepLink);
      setStep('waiting');
      startPolling(data.verificationId);
      if (data.deepLink && whatsappWindow) whatsappWindow.location.href = data.deepLink;
      else if (whatsappWindow) whatsappWindow.close();
    } catch (err) {
      if (whatsappWindow) whatsappWindow.close();
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await authAPI.getWhatsAppVerificationStatus(id);
        const status = res.data.status;
        if (status === 'VERIFIED') {
          clearInterval(pollRef.current);
          setStep('pin');
        } else if (status === 'EXPIRED' || status === 'FAILED') {
          clearInterval(pollRef.current);
          setStep('form');
          setError(status === 'EXPIRED'
            ? 'انتهت صلاحية رمز التحقق — حاول من جديد'
            : 'تعذّر التحقق — تأكد من إرسال الرمز الصحيح وحاول من جديد');
        }
      } catch { /* transient — keep polling */ }
    }, 3000);
  };

  const submitPin = async (e) => {
    e.preventDefault();
    if (pin.length !== 6) { setError('رمز PIN يجب أن يكون 6 أرقام'); return; }
    if (pin !== confirmPin) { setError('الرمزان غير متطابقين'); return; }
    if (!agreedToTerms) { setError('يجب الموافقة على الشروط أولاً'); return; }
    setError(''); setLoading(true);
    try {
      const res = await authAPI.completeWhatsAppRegistration({ verificationId, pin });
      const { token, ...safeUser } = res.data.data;
      localStorage.setItem('passenger_user', JSON.stringify(safeUser));
      navigate(returnTo || '/');
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 text-red-600 rounded-xl px-4 py-3 text-sm">
          <span className="text-lg">⚠️</span><span>{error}</span>
        </div>
      )}

      {step === 'form' && (
        <form onSubmit={start} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">الاسم الرباعي الكامل</label>
            <input type="text" required autoFocus value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
            <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
              <span className="text-amber-500 text-base leading-none mt-0.5">⚠️</span>
              <div>
                <p className="text-xs font-bold text-amber-800">الاسم الرباعي إلزامي</p>
                <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                  أدخل اسمك كاملاً رباعياً (4 كلمات) كما هو مدوّن في وثائقك الرسمية — جواز السفر أو بطاقة الهوية الوطنية. هذا الاسم سيُطبع على التذكرة.
                </p>
              </div>
            </div>
            {name.trim() && !isFullName && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <span>✗</span> الاسم غير مكتمل — أدخل 4 كلمات على الأقل
              </p>
            )}
            {name.trim() && isFullName && (
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <span>✓</span> الاسم الرباعي مكتمل
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">رقم الهاتف</label>
            <input type="tel" placeholder="+249 91 234 5678" required value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} dir="ltr" />
          </div>
          <button type="submit" disabled={loading}
            className="w-full bg-[#16A34A] hover:bg-[#15803D] disabled:opacity-50 text-white font-bold py-3.5 rounded-xl transition-all">
            {loading ? '...جارٍ الإرسال' : 'التحقق عبر واتساب'}
          </button>
        </form>
      )}

      {step === 'waiting' && (
        <div className="space-y-4 text-center">
          <p className="text-sm font-semibold text-gray-800">فتح واتساب تلقائياً برسالة جاهزة</p>
          <p className="text-xs text-gray-500">إن لم تُفتح نافذة واتساب (قد يحجبها المتصفح)، اضغط الزر أدناه أو أرسل الرمز يدوياً</p>
          <div className="bg-gray-50 border border-gray-200 rounded-xl py-4">
            <span className="text-xl font-bold tracking-widest text-[#1E40AF]" dir="ltr">SAFER-VFY-{code}</span>
          </div>
          <a href={deepLink || '#'} target="_blank" rel="noopener noreferrer"
            className="block w-full bg-[#16A34A] hover:bg-[#15803D] text-white font-bold py-3.5 rounded-xl transition-all">
            إعادة فتح واتساب
          </a>
          <p className="text-xs text-gray-500">بانتظار التأكيد...</p>
        </div>
      )}

      {step === 'pin' && (
        <form onSubmit={submitPin} className="space-y-4">
          <p className="text-sm font-semibold text-gray-800 text-center">تم التحقق — أنشئ رمز PIN</p>
          <input type="password" inputMode="numeric" maxLength={6} placeholder="••••••" required
            value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} className={pinInputClass} dir="ltr" />
          <input type="password" inputMode="numeric" maxLength={6} placeholder="تأكيد الرمز" required
            value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))} className={pinInputClass} dir="ltr" />

          <div className="bg-[#F5F9FF] border border-[#E5E7EB] rounded-xl px-4 py-3 flex items-start gap-3">
            <input
              id="whatsapp-terms-checkbox"
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-blue-700 flex-shrink-0 cursor-pointer"
            />
            <label htmlFor="whatsapp-terms-checkbox" className="text-xs text-gray-600 leading-relaxed cursor-pointer select-none">
              أوافق على{' '}
              <button type="button" onClick={() => setShowTerms(true)}
                className="text-blue-700 underline underline-offset-2 font-semibold hover:text-blue-900 transition-colors">
                شروط الخدمة وسياسة الخصوصية
              </button>
              {' '}الخاصة بمنصة سافر
            </label>
          </div>

          <button type="submit" disabled={loading || !agreedToTerms}
            className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-bold py-3.5 rounded-xl transition-all">
            {loading ? '...جارٍ التأكيد' : agreedToTerms ? 'تأكيد وإنشاء الحساب' : 'يجب الموافقة على الشروط أولاً'}
          </button>
        </form>
      )}
    </div>
  );
};

export default WhatsAppRegisterPanel;
